/**
 * VehicleAttachmentsTab - Aba "Anexos" do modal de veículos
 *
 * Agrega em formato de grid todos os arquivos anexados ao veículo,
 * independentemente da origem: anexos diretos ou andamentos.
 * Cópia fiel da aba de tarefas (TaskAttachmentsTab), trocando task → vehicle.
 *
 * Reaproveita:
 *  - RPC server-side list_vehicle_attachments (tenant-scoped)
 *  - AttachmentThumbnail (miniatura padrão com download/hover)
 *  - useVehicleNotes().deleteAttachment (para exclusão)
 *  - ImageLightbox para preview interno de imagens
 *
 * @version 1.0.0
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (paridade com TaskAttachmentsTab)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paperclip,
  Image as ImageIcon,
  FileText,
  FileSpreadsheet,
  File as FileIcon,
  Files,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';
import { resolvePreviewMediaType, downloadAttachmentFile, type PreviewMediaType } from '@/lib/attachment-preview';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  useVehicleActivityAttachments,
  type VehicleAttachmentTypeFilter,
  type VehicleActivityAttachment,
} from '@/hooks/useVehicleActivityAttachments';
import { useVehicleNotes } from '@/hooks/useVehicleDetails';

/** Props do componente */
interface VehicleAttachmentsTabProps {
  /** ID do veículo */
  vehicleId: string;
  /** Permite excluir anexos */
  canDelete?: boolean;
  /** True quando a aba está ativa (habilita fetch lazy) */
  isActive?: boolean;
  /** Callback opcional para navegar até a aba onde a timeline de andamentos vive */
  onNavigateToTimeline?: () => void;
  /** Callback opcional para expor totalCount ao pai (contador da aba) */
  onCountChange?: (count: number) => void;
}

/** Configuração dos chips de filtro */
const FILTERS: Array<{
  value: VehicleAttachmentTypeFilter;
  label: string;
  icon: typeof Files;
}> = [
  { value: 'all', label: 'Todos', icon: Files },
  { value: 'image', label: 'Imagens', icon: ImageIcon },
  { value: 'pdf', label: 'PDFs', icon: FileText },
  { value: 'document', label: 'Documentos', icon: FileText },
  { value: 'spreadsheet', label: 'Planilhas', icon: FileSpreadsheet },
  { value: 'other', label: 'Outros', icon: FileIcon },
];

/** Rótulo amigável por origem do anexo */
function sourceLabel(a: VehicleActivityAttachment): string {
  if (a.source_type === 'vehicle') return 'Anexos do veículo';
  return a.source_title || 'Andamento';
}

/**
 * Aba "Anexos" — grid de miniaturas com filtro por tipo e paginação incremental
 */
export function VehicleAttachmentsTab({
  vehicleId,
  canDelete = true,
  isActive = true,
  onNavigateToTimeline,
  onCountChange,
}: VehicleAttachmentsTabProps) {
  const [typeFilter, setTypeFilter] = useState<VehicleAttachmentTypeFilter>('all');
  // Estado do preview interno (imagem, vídeo ou documento/PDF via iframe)
  const [lightbox, setLightbox] = useState<{
    open: boolean;
    src: string;
    alt: string;
    type: PreviewMediaType;
  }>({ open: false, src: '', alt: '', type: 'image' });

  // Hook principal — lazy: só busca quando a aba está ativa
  const {
    attachments,
    totalCount,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useVehicleActivityAttachments(vehicleId, typeFilter, { enabled: isActive });

  // Mutation de exclusão (delega para o hook central de andamentos do veículo)
  const { deleteAttachment } = useVehicleNotes(vehicleId);

  // Expor contador ao pai (para badge na aba)
  useEffect(() => {
    if (typeFilter === 'all') {
      onCountChange?.(totalCount);
    }
  }, [totalCount, typeFilter, onCountChange]);

  // Infinite scroll — observer no sentinel do final da lista
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, attachments.length]);

  // Agrupamento por origem (vehicle | note) para dar contexto na UI
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { head: VehicleActivityAttachment; items: VehicleActivityAttachment[] }
    >();
    for (const att of attachments) {
      const key = `${att.source_type}:${att.source_id}`;
      const existing = map.get(key);
      if (existing) {
        existing.items.push(att);
      } else {
        map.set(key, { head: att, items: [att] });
      }
    }
    return Array.from(map.values());
  }, [attachments]);

  // Handler ao clicar em uma miniatura
  const handleClickAttachment = async (att: VehicleActivityAttachment) => {
    // Preview interno para imagem/vídeo/PDF; demais formatos são baixados
    const mediaType = resolvePreviewMediaType(att.file_type, att.mime_type);
    if (mediaType) {
      setLightbox({ open: true, src: att.public_url, alt: att.file_name, type: mediaType });
      return;
    }
    try {
      await downloadAttachmentFile(att.public_url, att.file_name);
    } catch {
      toast.error('Erro ao baixar arquivo');
    }
  };

  // Skeleton inicial
  if (isLoading && attachments.length === 0) {
    return (
      <div className="space-y-3">
        <SkeletonBar />
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com contador + filtros */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {totalCount} {totalCount === 1 ? 'anexo' : 'anexos'}
            </span>
          </div>
        </div>

        {/* Barra de filtros por tipo */}
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const active = typeFilter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setTypeFilter(f.value)}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all',
                  active
                    ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                    : 'bg-muted/60 text-muted-foreground hover:bg-muted',
                )}
              >
                <Icon className="h-3 w-3" />
                <span>{f.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Estado vazio */}
      {attachments.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="p-3 rounded-full bg-muted mb-3">
            <Paperclip className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-medium mb-1">Nenhum anexo</h3>
          <p className="text-xs text-muted-foreground max-w-xs">
            {typeFilter === 'all'
              ? 'Este veículo ainda não tem arquivos anexados.'
              : 'Nenhum arquivo desse tipo foi anexado a este veículo.'}
          </p>
        </div>
      )}

      {/* Grupos por origem */}
      {grouped.map((group) => {
        const head = group.head;
        const isFromNote = head.source_type !== 'vehicle';
        return (
          <div key={`${head.source_type}:${head.source_id}`} className="space-y-2">
            <div className="flex items-center justify-between gap-2 border-b pb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium truncate">{sourceLabel(head)}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  ·{' '}
                  {formatDistanceToNow(new Date(head.source_created_at), {
                    addSuffix: true,
                    locale: ptBR,
                  })}
                </span>
              </div>
              {isFromNote && onNavigateToTimeline && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] px-2 shrink-0"
                  onClick={() => onNavigateToTimeline()}
                >
                  Ver andamento
                </Button>
              )}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {group.items.map((att) => (
                <AttachmentThumbnail
                  key={att.id}
                  attachment={{
                    id: att.id,
                    file_name: att.file_name,
                    file_type: att.file_type,
                    public_url: att.public_url,
                    thumbnail_url: att.thumbnail_url,
                    file_size: att.file_size,
                  }}
                  className="w-full h-auto aspect-square"
                  readOnly={!canDelete}
                  onClick={() => handleClickAttachment(att)}
                  onDelete={canDelete ? () => deleteAttachment.mutate(att.id) : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Sentinel do infinite scroll */}
      {hasNextPage && (
        <div ref={sentinelRef} className="flex items-center justify-center py-4">
          {isFetchingNextPage && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      )}

      {/* Preview interno (imagem, vídeo ou documento) */}
      <ImageLightbox
        open={lightbox.open}
        onClose={() => setLightbox((s) => ({ ...s, open: false }))}
        mediaItems={[{ url: lightbox.src, type: lightbox.type, title: lightbox.alt }]}
        caption={lightbox.alt}
      />
    </div>
  );
}

/** Barra skeleton usada durante o loading inicial */
function SkeletonBar() {
  return (
    <div className="flex gap-2">
      <div className="h-6 w-16 rounded-full bg-muted animate-pulse" />
      <div className="h-6 w-20 rounded-full bg-muted animate-pulse" />
      <div className="h-6 w-16 rounded-full bg-muted animate-pulse" />
    </div>
  );
}
